// frontend/src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { SearchProvider } from './contexts/SearchContext'; // Add this import
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { TriggerProvider } from './contexts/TriggerContext';
import PrivateRoute from './components/PrivateRoute';
import EducatorRoutes from './components/EducatorRoutes';
import AdminDashboard from './pages/Admin/AdminDashboard';
import Layout from './components/Layout';
import { initCsrfToken } from './services/api';

// Page imports
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import VerifyEmail from './pages/Auth/VerifyEmail';
import EmailVerificationPending from './pages/Auth/EmailVerificationPending';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Home from './pages/Home/Home';
import Search from './pages/Search/Search';
import Communities from './pages/Communities/Communities';
import Notifications from './pages/Notifications/Notifications';
import Profile from './pages/Profile/Profile';
import Settings from './pages/Settings/Settings';
import QuestionDetail from './pages/Questions/QuestionDetail';
import WorkedExample from './pages/WorkedExamples/WorkedExample';
import CreateContent from './pages/Create/CreateContent';
import Pricing from './pages/Pricing/Pricing';
import SubscriptionSuccess from './pages/Pricing/SubscriptionSuccess';
import SubscriptionManagement from './pages/Settings/SubscriptionManagement';
import SmartStudy from './pages/SmartStudy/SmartStudy';
import AnalyticsDashboard from './pages/Analytics/AnalyticsDashboard';
import StudySessionPage from './pages/StudySession/StudySession';
import CertificatesPage from './pages/Certificates/Certificates';
import StudyPlanPage from './pages/StudyPlan/StudyPlan';
import ExamCountdownPage from './pages/ExamCountdown/ExamCountdown';
import GoalsPage from './pages/Goals/Goals';
import WeeklyReportsPage from './pages/WeeklyReports/WeeklyReports';

import './App.css';
import TeacherDashboard from './pages/educator/TeacherDashboard';
import { StudyRoomProvider } from './contexts/StudyRoomContext';
import StudyRooms from './pages/StudyRooms/StudyRooms';
import StudyRoomWidget from './components/studyroom/StudyRoomWidget';


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  useEffect(() => {
    initCsrfToken();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SubscriptionProvider><TriggerProvider>
        <Router>
          <SearchProvider> {/* Add SearchProvider here, inside Router */}
            <Routes>
              {/* Public routes */}
              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/email-pending" element={<EmailVerificationPending />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Subscription routes (public) */}
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              {/* Protected routes */}
              <Route element={<PrivateRoute />}>
                <Route element={<Layout />}>
                  <Route path="/home" element={<Home />} />
                  <Route path="/search" element={<Search />} />
                  <Route path="/communities" element={<Communities />} />
                  <Route path="/notifications" element={<Notifications />} />
              <Route path="/study-rooms" element={<StudyRoomProvider><StudyRooms /></StudyRoomProvider>} />
                  <Route path="/profile/:username" element={<Profile />} />
                  <Route path="/create" element={<CreateContent />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings/subscription" element={<SubscriptionManagement />} />
                  <Route path="/questions/:id" element={<QuestionDetail />} />
                  <Route path="/examples/:id" element={<WorkedExample />} />
                  <Route path="/smart-study" element={<SmartStudy />} />
                  <Route path="/analytics" element={<AnalyticsDashboard />} />
                  <Route path="/study-session" element={<StudySessionPage />} />
                  <Route path="/certificates" element={<CertificatesPage />} />
                <Route path="/study-plan" element={<StudyPlanPage />} />
                <Route path="/exam-countdown" element={<ExamCountdownPage />} />
                <Route path="/goals" element={<GoalsPage />} />
                <Route path="/weekly-reports" element={<WeeklyReportsPage />} />
                  
                  {/* Educator routes */}
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/educator/*" element={<EducatorRoutes />} />
                </Route>
              </Route>
              
              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="*" element={<Navigate to="/home" replace />} />
            <Route path="/educator/dashboard" element={<TeacherDashboard />} />
</Routes>
          </SearchProvider> {/* Close SearchProvider here */}
        </Router>
      </TriggerProvider></SubscriptionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
